import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Users, GitFork, LogIn, RefreshCw, Award } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color = '#1086a4' }) => (
  <div className="bg-card rounded-lg p-4 border border-border">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-sm text-foreground/60">{label}</p>
        <p className="text-2xl font-bold text-foreground">{value}</p>
      </div>
    </div>
  </div>
);

const SourceAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideNotTracked, setHideNotTracked] = useState(true);
  const token = useSelector(state => state.auth.token);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/dashboard/source-analytics', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load source analytics:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
      </div>
    );
  }

  if (!data) {
    return <p className="text-foreground/60 text-center py-8">Failed to load analytics data.</p>;
  }

  const sourceLabels = {
    'invitation': 'Invitation',
    'referral': 'Referral Link',
    'group-code': 'Group Code',
    'self-register': 'Self-Register (External)',
    'visitor': 'Visitor',
    'direct': 'Direct',
    'unknown': 'Not Tracked (Before Tracking)',
    'null': 'Not Tracked (Before Tracking)'
  };

  const sourceColors = {
    'invitation': '#1086a4',
    'referral': '#f59e0b',
    'group-code': '#8b5cf6',
    'self-register': '#10b981',
    'visitor': '#6b7280',
    'direct': '#054581',
    'unknown': '#9ca3af'
  };

  const isNotTracked = (source) => !source || source === 'unknown' || source === 'null' || source === 'direct';

  // Always hide not-tracked from source breakdown chart (it's just noise from legacy users)
  const filteredBreakdown = Object.fromEntries(
    Object.entries(data.sourceBreakdown).filter(([s]) => !isNotTracked(s))
  );

  const filteredSignups = hideNotTracked
    ? data.recentSignups.filter(u => !isNotTracked(u.registrationSource))
    : data.recentSignups;

  const totalSignups = Object.values(filteredBreakdown).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">User Source Tracking</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideNotTracked}
              onChange={(e) => setHideNotTracked(e.target.checked)}
              className="rounded border-border text-[#1086a4] focus:ring-[#1086a4]"
            />
            Hide Not Tracked
          </label>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Signups" value={totalSignups} />
        <StatCard icon={LogIn} label="Logins (30d)" value={data.loginsLast30Days} color="#10b981" />
        <StatCard icon={GitFork} label="Paths Completed" value={data.pathCompletions} color="#8b5cf6" />
        <StatCard icon={Award} label="IBM Claimed" value={data.ibmClaimedCount || 0} color="#054581" />
      </div>

      {/* External Link Clicks */}
      {data.externalClicks.length > 0 && (
        <div className="bg-card rounded-lg p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">External Link Clicks</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 text-foreground/60 font-medium">URL</th>
                  <th className="text-right py-2 text-foreground/60 font-medium">Clicks</th>
                  <th className="text-right py-2 text-foreground/60 font-medium">Last Click</th>
                </tr>
              </thead>
              <tbody>
                {data.externalClicks.map((click, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-2 text-foreground truncate max-w-xs">{click.label || click._id}</td>
                    <td className="py-2 text-right text-foreground font-medium">{click.count}</td>
                    <td className="py-2 text-right text-foreground/60">{new Date(click.lastClicked).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Path Progression */}
      {data.pathStarts.length > 0 && (
        <div className="bg-card rounded-lg p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Path Progression</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.pathStarts.map((path, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-foreground font-medium">{path._id || 'Unknown'}</span>
                <span className="text-sm bg-[#1086a4] text-white px-2 py-0.5 rounded-full">{path.count} started</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Signups */}
      <div className="bg-card rounded-lg p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Signups</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-foreground/60 font-medium">Name</th>
                <th className="text-left py-2 text-foreground/60 font-medium">Email</th>
                <th className="text-left py-2 text-foreground/60 font-medium">Program</th>
                <th className="text-left py-2 text-foreground/60 font-medium">Source</th>
                <th className="text-left py-2 text-foreground/60 font-medium">Referred By</th>
                <th className="text-right py-2 text-foreground/60 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredSignups.map((user, i) => (
                <tr key={i} className="border-b border-border/50">
                  <td className="py-2 text-foreground">{user.name} {user.lastName || ''}</td>
                  <td className="py-2 text-foreground/60">{user.email}</td>
                  <td className="py-2 text-foreground/60">{user.group?.program?.title || '-'}</td>
                  <td className="py-2">
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: sourceColors[user.registrationSource] || '#6b7280' }}
                    >
                      {sourceLabels[user.registrationSource] || user.registrationSource || 'Not Tracked'}
                    </span>
                  </td>
                  <td className="py-2 text-foreground/60">
                    {user.referredBy ? `${user.referredBy.name} ${user.referredBy.lastName || ''}` : '-'}
                  </td>
                  <td className="py-2 text-right text-foreground/60">{new Date(user.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SourceAnalytics;
